import React, { useEffect, useRef } from 'react';

/**
 * SectionShape — a small family of rhyming background shapes that give the
 * page depth and motion without the paid GSAP MorphSVGPlugin.
 *
 * They share one visual language (the KACHNG green ring gradient
 * #1F7A4D -> #0E2C1B) so the page feels composed: a circle behind the Hero,
 * a large ring at Truth, diagonal bands mid-page, a "pulse" at Ask, a
 * flowing ribbon at Rewards, and the circle again at Close (bookend).
 *
 * Motion is free CSS: slow rotate / drift / breathe. All shapes are
 * decorative (aria-hidden) and never intercept clicks.
 *
 * Keyframes used (add to globals.css — see the block returned separately):
 *   shapeSpin, shapeDrift, shapeBreathe
 * All are disabled under prefers-reduced-motion via globals.css.
 */

const RING_BRIGHT = '#1F7A4D';
const RING_FAINT = '#0E2C1B';

const base = {
  position: 'absolute',
  pointerEvents: 'none',
  userSelect: 'none',
  zIndex: 0,
};

// A ring drawn as an SVG stroke. `sweep` controls how much of the circle
// is filled (270 = 3/4). `draw` animates the arc sweeping in on mount.
// `bright` switches from the deep muted gradient to the brand green pop.
// Outer div = position, inner div = spin (transforms kept separate).
function Ring({
  size = 600,
  thickness = 64,
  opacity = 0.34,
  blur = 5,
  spin = true,
  sweep = 270,
  draw = false,
  bright = false,
  style,
}) {
  const dim = `min(${size}px, 120vw)`;
  const sw = (thickness / size) * 100;   // stroke width in viewBox units
  const r = 50 - sw / 2;

  const arcRef = useRef(null);

  useEffect(() => {
    if (!draw) return;
    const el = arcRef.current;
    if (!el) return;
    // reduced-motion users get the ring already full — no sweep
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    el.animate(
      [
        { strokeDasharray: '0 360' },
        { strokeDasharray: `${sweep} ${360 - sweep}` },
      ],
      { duration: 1400, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'backwards' }
    );
  }, [draw, sweep]);// radius so the stroke hugs the edge

  return (
    <div
      aria-hidden="true"
      data-shape="ring"
      style={{
        ...base,
        width: dim,
        height: dim,
        opacity,
        filter: `blur(${blur}px)`,
        ...style,
      }}
    >
      <div
        data-shape="ring-spin"
        style={{
          width: '100%',
          height: '100%',
          animation: spin ? 'shapeSpin 60s linear infinite' : undefined,
        }}
      >
        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%', display: 'block' }}>
          <defs>
            <linearGradient id="kringBright" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00E676" />
              <stop offset="100%" stopColor="#1F7A4D" />
            </linearGradient>
            <linearGradient id="kringDeep" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1F7A4D" />
              <stop offset="100%" stopColor="#0E2C1B" />
            </linearGradient>
          </defs>
          {/* faint full-circle track — makes the unfilled quarter legible */}
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={`url(#${bright ? 'kringBright' : 'kringDeep'})`}
            strokeWidth={sw}
            opacity="0.18"
          />
          <circle
            ref={arcRef}
            cx="50"
            cy="50"
            r={r}
            pathLength="360"
            fill="none"
            stroke={`url(#${bright ? 'kringBright' : 'kringDeep'})`}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeDasharray={`${sweep} ${360 - sweep}`}
            transform="rotate(-90 50 50)"
          />
        </svg>
      </div>
    </div>
  );
}

// A soft diagonal band that sweeps across mid-page sections.
// Wrapped in a full-cover container so it works in any positioned parent
// (grid, flex, or block) without becoming a layout item itself.
function Band({ opacity = 0.5, style }) {
  return (
    <div
      aria-hidden="true"
      data-shape="band"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '-30%',
          width: '160%',
          height: 'clamp(120px, 18vw, 240px)',
          transform: 'translateY(-50%) rotate(-8deg)',
          background: `linear-gradient(90deg, transparent 0%, ${RING_FAINT} 20%, #10402A 45%, #10402A 65%, ${RING_FAINT} 85%, transparent 100%)`,
          opacity,
          filter: 'blur(2px)',
          animation: 'shapeDrift 18s ease-in-out infinite alternate',
        }}
      />
    </div>
  );
}

// A flowing ribbon (two stacked skewed bands) for the Rewards section.
// A flowing ribbon for Rewards — an elegant U-shaped curve (real SVG path,
// same construction as Growth) rather than a rotated/blurred rectangle.
// Foreshadows the ring it resolves into at Close.
function Ribbon({ opacity = 0.5, style }) {
  return (
    <div
      aria-hidden="true"
      data-shape="ribbon"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        opacity,
        ...style,
      }}
    >
      <svg
        viewBox="0 0 1200 600"
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <defs>
          <linearGradient id="kribbonFill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={RING_FAINT} stopOpacity="0" />
            <stop offset="35%" stopColor="#124A2E" stopOpacity="0.55" />
            <stop offset="65%" stopColor="#124A2E" stopOpacity="0.55" />
            <stop offset="100%" stopColor={RING_FAINT} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* the elegant U — a single smooth stroke */}
        <path
          d="M-60 60 C 220 60, 380 460, 600 460 S 1000 60, 1260 60"
          fill="none"
          stroke={RING_BRIGHT}
          strokeWidth="175"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

// A large soft "pulse" mark for Ask — a question-mark motif (see note).
// Rendered as an outline SVG so it reads as a symbol, gently breathing.
// A large magnifying-glass motif for the Ask section — reads as "search
// your own data." Self-covering wrapper so it fills any positioned parent;
// gently breathes (scale) rather than sitting fully static.
function Magnify({ opacity = 0.22, style }) {
  return (
    <div
      aria-hidden="true"
      data-shape="magnify"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        ...style,
      }}
    >
      <svg
        viewBox="0 0 950 460"
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: 'absolute',
          right: '55%',
          top: '50%',
          transform: 'translateY(-50%) ',
          width: 'min(3800px, 400vw)',
          height: 'auto',
          opacity
        }}
      >
        <circle cx="640" cy="200" r="150" fill="none" stroke={RING_BRIGHT} strokeWidth="14" />
        <line x1="748" y1="308" x2="880" y2="440" stroke={RING_BRIGHT} strokeWidth="20" strokeLinecap="round" />
      </svg>
    </div>
  );
}

const SHAPES = { ring: Ring, band: Band, ribbon: Ribbon, magnify: Magnify, growth: Growth, resolve: Resolve };

// A rising growth curve for the Spending section — an ascending line that
// climbs left-to-right with a soft area fill beneath, symbolizing growth.
// Self-covering wrapper so it fills any positioned parent.
function Growth({ opacity = 0.5, style }) {
  return (
    <div
      aria-hidden="true"
      data-shape="growth"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        opacity,
        ...style,
      }}
    >
      <svg
        viewBox="0 0 1200 600"
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <defs>
          <linearGradient id="kgrowthFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10402A" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#10402A" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* area fill under the climbing curve */}
        <path
          d="M-60 540 C 60 512, 150 522, 250 488 C 350 454, 400 466, 500 420 C 600 374, 650 396, 760 330 C 870 264, 920 288, 1030 200 C 1110 136, 1180 92, 1260 40 L 1260 600 L -60 600 Z"
          fill="url(#kgrowthFill)"
        />
        {/* the mountain climb — keeps ascending steeply through the trailing edge (no flatten) */}
        <path
          d="M-60 540 C 60 512, 150 522, 250 488 C 350 454, 400 466, 500 420 C 600 374, 650 396, 760 330 C 870 264, 920 288, 1030 200 C 1110 136, 1180 92, 1260 40"
          fill="none"
          stroke="#1F7A4D"
          strokeWidth="3"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

// The resolving waveform for Ask — the card's equalizer badge at macro
// scale. Tall, erratic bars on the left ("where did my money go?")
// settle into short, calm, even bars on the right (the answer).
// Bars are stroked lines with round caps + non-scaling-stroke, so
// their width stays crisp even though the SVG stretches full-bleed.
const RESOLVE_BARS = Array.from({ length: 44 }, (_, i) => {
  const t = i / 43;                              // 0 → 1 across the section
  const amp = 30 + 240 * Math.pow(1 - t, 1.7);   // amplitude decays left → right
  const jitter =
    0.35 + 0.65 * Math.abs(Math.sin(i * 1.7 + Math.sin(i * 0.9) * 1.3));
  return { x: -40 + i * 30, h: Math.max(16, amp * jitter) };
});

function Resolve({ opacity = 0.1, style }) {
  return (
    <div
      aria-hidden="true"
      data-shape="resolve"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        opacity,
        ...style,
      }}
    >
      <svg
        viewBox="0 0 1200 600"
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <defs>
          {/* userSpaceOnUse: vertical lines have zero-width bounding boxes,
      which makes bbox-relative gradients render as nothing */}
          <linearGradient
            id="kresolveStroke"
            gradientUnits="userSpaceOnUse"
            x1="0" y1="0" x2="1200" y2="0"
          >
            <stop offset="0%" stopColor="#1F7A4D" stopOpacity="0.4" />
            <stop offset="60%" stopColor="#1F7A4D" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#1F7A4D" stopOpacity="1" />
          </linearGradient>
        </defs>
        {RESOLVE_BARS.map((bar, i) => (
          <line
            key={i}
            x1={bar.x}
            y1={300 - bar.h / 2}
            x2={bar.x}
            y2={300 + bar.h / 2}
            stroke="url(#kresolveStroke)"
            strokeWidth="7"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
    </div>
  );
}

/**
 * <SectionShape kind="ring" position={{...}} ...props />
 * `position` overrides placement (top/left/right/bottom/transform).
 */
export default function SectionShape({ kind = 'ring', position = {}, ...props }) {
  const Shape = SHAPES[kind];
  if (!Shape) return null;
  return <Shape style={position} {...props} />;
}